// Shared term-matrix layout for the Term-Based grade report (all roles).
// One table per term: rows = subjects, columns = Subject | each component type | Term Paper | Total Marks | Percentage.
// Every cell shows the combined `obtained/max` (already scaled to its own % share when one's set); no marks -> dash.
// Followed by a compact "Overall" table (Subject | Marks | Percentage), an Overall Marks line and the Overall Percentage. Nothing is recomputed client-side (data = response.termMatrix).
//
// Exports:
//   default TermMatrixTables({ matrix })   - React rendering (used inside the on-screen report card)
//   termMatrixHTML(matrix)                 - same layout as an HTML string (bulk print / PDF)

const DASH = '—';
const hasVal = (v) => v !== null && v !== undefined && v !== '';
const num = (v) => Number(v);
const pctText = (p) => (hasVal(p) ? num(p) + '%' : DASH);
const markText = (o, m) => num(o) + '/' + num(m);
const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const termMatrixCss = `
  .tm-term { border-bottom:1px solid #1a1d2e; }
  .tm-term-head { background:#f0f0f0; padding:6px 14px; border-bottom:1px solid #1a1d2e; display:flex; align-items:baseline; justify-content:space-between; flex-wrap:wrap; gap:4px 12px; }
  .tm-term-name { font-size:.84rem; font-weight:700; text-transform:uppercase; letter-spacing:.04em; }
  .tm-term-meta { font-size:.68rem; font-weight:400; color:#555; }
  .tm-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  .tm-table { width:100%; border-collapse:collapse; font-size:.76rem; }
  .tm-table th { background:#e8e8e8; padding:5px 10px; text-align:center; border:1px solid #ccc; font-weight:700; font-size:.7rem; white-space:nowrap; }
  .tm-table td { padding:5px 10px; text-align:center; border:1px solid #ddd; vertical-align:middle; white-space:nowrap; }
  .tm-table th.tm-l, .tm-table td.tm-l { text-align:left; }
  .tm-table td.tm-subj { font-weight:600; }
  .tm-table td.tm-total { background:#f8f8f8; font-weight:700; }
  .tm-sub { display:block; font-size:.62rem; font-weight:400; color:#777; line-height:1.2; margin-top:1px; }
  .tm-dash { color:#999; }
  .tm-overall-head { background:#f0f0f0; padding:6px 14px; font-size:.8rem; font-weight:700; border-bottom:1px solid #1a1d2e; }
  .tm-ov-wrap { overflow-x:auto; }
  .tm-ov-line { display:flex; align-items:baseline; justify-content:space-between; gap:12px; padding:7px 14px; border-top:1px solid #ddd; font-size:.8rem; }
  .tm-ov-line-lbl { font-weight:700; text-transform:uppercase; letter-spacing:.04em; font-size:.72rem; }
  .tm-ov-line-val { font-weight:700; }
`;

const Cell = ({ c, cls }) => (
  hasVal(c && c.obtained) && c.max != null
    ? <td className={cls}>{markText(c.obtained, c.max)}</td>
    : <td className={cls}><span className="tm-dash">{DASH}</span></td>
);

const TotalCells = ({ t }) => (
  t && hasVal(t.obtained) && t.max != null
    ? (
      <>
        <td className="tm-total">{num(t.obtained)} / {num(t.max)}</td>
        <td className="tm-total">{pctText(t.percentage)}</td>
      </>
    )
    : (
      <>
        <td className="tm-total"><span className="tm-dash">{DASH}</span></td>
        <td className="tm-total"><span className="tm-dash">{DASH}</span></td>
      </>
    )
);

export default function TermMatrixTables({ matrix }) {
  if (!matrix) return null;
  const terms = matrix.terms || [];
  const overall = matrix.overall || {};
  return (
    <>
      <style>{termMatrixCss}</style>
      {terms.map((t, ti) => {
        const cols = t.columns || [];
        const span = 3 + cols.length + (t.hasPaper ? 1 : 0);
        return (
          <div key={t.termId != null ? t.termId : ti} className="tm-term">
            <div className="tm-term-head">
              <span className="tm-term-name">{t.termName}</span>
            </div>
            <div className="tm-wrap">
              <table className="tm-table">
                <thead>
                  <tr>
                    <th className="tm-l">Subject</th>
                    {cols.map(c => <th key={c}>{c}</th>)}
                    {t.hasPaper && <th>Term Paper</th>}
                    <th>Total Marks</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {(t.rows || []).length === 0 ? (
                    <tr><td colSpan={span}><span className="tm-dash">{DASH}</span></td></tr>
                  ) : (t.rows || []).map((r, ri) => (
                    <tr key={r.subject || ri}>
                      <td className="tm-l tm-subj">{r.subject}</td>
                      {cols.map(c => <Cell key={c} c={r.cells && r.cells[c]} />)}
                      {t.hasPaper && <Cell c={r.paper} />}
                      <TotalCells t={r.total} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="tm-term">
        <div className="tm-overall-head">Overall</div>
        <div className="tm-ov-wrap">
          <table className="tm-table">
            <thead>
              <tr>
                <th className="tm-l">Subject</th>
                <th>Marks</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {(overall.subjects || []).map((s, i) => (
                <tr key={s.subject || i}>
                  <td className="tm-l tm-subj">{s.subject}</td>
                  {hasVal(s.obtained) && s.max != null
                    ? <td>{num(s.obtained)} / {num(s.max)}</td>
                    : <td><span className="tm-dash">{DASH}</span></td>}
                  <td>{hasVal(s.percentage) ? pctText(s.percentage) : <span className="tm-dash">{DASH}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tm-ov-line">
          <span className="tm-ov-line-lbl">Overall Marks</span>
          <span className="tm-ov-line-val">
            {hasVal(overall.obtained) && overall.max != null ? num(overall.obtained) + ' / ' + num(overall.max) : DASH}
          </span>
        </div>
      </div>
    </>
  );
}

// ─── HTML string version (bulk print / PDF) ──────────────────────────────────
export function termMatrixHTML(matrix) {
  if (!matrix) return '';
  const th = (txt, left) => `<th style="background:#e8e8e8;padding:4px 8px;text-align:${left ? 'left' : 'center'};border:1px solid #ccc;font-size:11px;font-weight:700;white-space:nowrap;">${esc(txt)}</th>`;
  const tdStyle = 'padding:4px 8px;text-align:center;border:1px solid #ddd;vertical-align:middle;';
  const dashTd = (extra) => `<td style="${tdStyle}${extra || ''}"><span style="color:#999;">${DASH}</span></td>`;
  const cellTd = (c) => (
    c && hasVal(c.obtained) && c.max != null
      ? `<td style="${tdStyle}">${markText(c.obtained, c.max)}</td>`
      : dashTd()
  );
  const totalTds = (t) => (
    t && hasVal(t.obtained) && t.max != null
      ? `<td style="${tdStyle}background:#f8f8f8;font-weight:700;">${num(t.obtained)} / ${num(t.max)}</td><td style="${tdStyle}background:#f8f8f8;font-weight:700;">${pctText(t.percentage)}</td>`
      : dashTd('background:#f8f8f8;') + dashTd('background:#f8f8f8;')
  );

  const termsHTML = (matrix.terms || []).map(t => {
    const cols = t.columns || [];
    const span = 3 + cols.length + (t.hasPaper ? 1 : 0);
    const rows = (t.rows || []).length === 0
      ? `<tr><td colspan="${span}" style="${tdStyle}"><span style="color:#999;">${DASH}</span></td></tr>`
      : (t.rows || []).map(r => `
          <tr>
            <td style="padding:4px 8px;text-align:left;border:1px solid #ddd;font-weight:600;">${esc(r.subject)}</td>
            ${cols.map(c => cellTd(r.cells && r.cells[c])).join('')}
            ${t.hasPaper ? cellTd(r.paper) : ''}
            ${totalTds(r.total)}
          </tr>`).join('');
    return `
      <div style="border-bottom:1px solid #111;page-break-inside:avoid;">
        <div style="background:#f0f0f0;padding:5px 14px;border-bottom:1px solid #111;display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:4px 12px;">
          <span style="font-size:12px;font-weight:700;text-transform:uppercase;">${esc(t.termName)}</span>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead><tr>
            ${th('Subject', true)}
            ${cols.map(c => th(c)).join('')}
            ${t.hasPaper ? th('Term Paper') : ''}
            ${th('Total Marks')}
            ${th('Percentage')}
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }).join('');

  const overall = matrix.overall || {};
  const ovRows = (overall.subjects || []).map(s => `
    <tr>
      <td style="padding:4px 8px;text-align:left;border:1px solid #ddd;font-weight:600;">${esc(s.subject)}</td>
      ${hasVal(s.obtained) && s.max != null ? `<td style="${tdStyle}">${num(s.obtained)} / ${num(s.max)}</td>` : dashTd()}
      ${hasVal(s.percentage) ? `<td style="${tdStyle}">${pctText(s.percentage)}</td>` : dashTd()}
    </tr>`).join('');
  const ovMarks = hasVal(overall.obtained) && overall.max != null ? num(overall.obtained) + ' / ' + num(overall.max) : DASH;

  return termsHTML + `
    <div style="border-bottom:1px solid #111;page-break-inside:avoid;">
      <div style="background:#f0f0f0;padding:5px 14px;font-size:12px;font-weight:700;border-bottom:1px solid #111;">Overall</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr>${th('Subject', true)}${th('Marks')}${th('Percentage')}</tr></thead>
        <tbody>${ovRows}</tbody>
      </table>
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding:6px 14px;border-top:1px solid #ddd;font-size:12px;">
        <span style="font-weight:700;text-transform:uppercase;font-size:11px;">Overall Marks</span>
        <span style="font-weight:700;">${ovMarks}</span>
      </div>
    </div>`;
}
